import React, { ReactElement, useContext, useState } from 'react'
import { RecentPeopleThumbnailList, TopPeopleThumbnailList } from '../Person/PersonThumbnailList'
import { library } from '@fortawesome/fontawesome-svg-core'
import { AppContext } from '../contexts/AppContext'
import { ExploreTab } from '../contexts/AppContext.d'
import Tabs, { TabSet } from '../common/Tabs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleRight, faCalendarWeek, faClockRotateLeft, faInfoCircle, faPen, faRotateLeft } from '@fortawesome/free-solid-svg-icons'
import { PersonContext } from '../contexts/PersonContext'
import { formatDate, formatNumberWithCommas, formatTimeInterval } from '../helpers/format'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import Graph from '../common/Graph'
import { GraphData } from '../common/graph/Graph.d'
import { countryFlagLabel, countryTooltipLabel } from '../helpers/countries'
import { EntryRetriever, SpanLoader } from '../reducers/EntryReducer'
import { PersonRetriever } from '../reducers/PersonReducer'
import { EntryContext } from '../contexts/EntryContext'
import { InfiniteScrollSpanHeader } from '../common/InfiniteScroll'
import { ExploreHelp } from './ExploreHelp'
import ExploreRatings from './ExploreRatings'
library.add(faArrowCircleRight, faInfoCircle, faClockRotateLeft, faCalendarWeek, faPen, faRotateLeft)

const RECENT_PEOPLE_CLOUD_DAYS = 30

const addMentionsPerCitizen = (data: GraphData): GraphData => ({
    ...data,
    title: 'Top countries and the names linked to them',
    fieldNames: { ...data.fieldNames, count: 'Cumulative mentions', citizens: 'Individual citizens' },
    xLabelFormatter: countryTooltipLabel,
    xTickFormatter: countryFlagLabel,
    data: data.data.map(row => ({
        ...row,
        mentionsPerCitizen: typeof row.count === 'number' && typeof row.citizens === 'number' && row.citizens > 0
            ? row.count / row.citizens
            : null
    }))
})

const exploreTabs: TabSet<ExploreTab> = {
    recent: {
        icon: 'clock-rotate-left',
        text: 'Recent'
    },
    allTime: {
        icon: 'calendar-week',
        text: 'All time'
    },
    help: {
        icon: 'info-circle',
        text: 'Help'
    }
}

export const ExploreUserStats = (props: { from?: number, until?: number }): ReactElement => {
    const { state } = useContext(EntryContext)
    const stats = state.stats?.[JSON.stringify({ type: 'QUERY_USER_STATS', from: props.from, until: props.until })]
    if (!stats) {
        return <></>
    }
    const entry_ies = stats.entries === 1 ? 'entry' : 'entries'
    const inQuery = props.from && props.until
        ? `${entry_ies} in this period`
        : props.from && !props.until
            ? `recent ${entry_ies}`
            : !props.from && props.until
                ? `${entry_ies} before then`
                : `${entry_ies} in total`
    const prepareNudge = stats.entriesUpdated === 0
        ? ''
        : <p className="tip">Note: entries may have been modified since this was calculated.
            {' '}You can <Link to="/settings">process them</Link> periodically.
        </p>
    // Carry the current Explore date range into the words link, so its graph and list match this scope.
    const dateParams = (props.from ? `&from=${formatDate(props.from)}` : '')
      + (props.until ? `&until=${formatDate(props.until)}` : '')
    if (stats.entries === 0) {
        return <div id="explore-user-stats">
            <p>You have <strong>0</strong> {inQuery}. Why not <Link to="/write">write one now</Link>?</p>
            {prepareNudge}
        </div>
    }
    return <div id="explore-user-stats">
        <p>You have <strong>{stats.entries}</strong> {inQuery}.
            {' '}{stats.entries === 1 ? 'It mentions' : 'They mention'} {formatNumberWithCommas(stats.peopleCountUnique)}
            {' '}{stats.peopleCountUnique === 1 ? 'person/tag' : 'people/tags'}
            {' '}a total of {formatNumberWithCommas(stats.peopleCountTotal)} times.
        </p>
        <p>You&apos;ve spent a total of {formatTimeInterval(stats.writeupTimeExactTotal)} actively writing
            {' '}<Link to={ `/entries/by/wordcount?order=wordcount&direction=desc${dateParams}` }>
                {formatNumberWithCommas(stats.wordcountTotal)} words
            </Link>
            {' '}in {stats.entries === 1 ? 'that entry' : 'those entries'}.
        </p>
        { stats.entries > 1 && <p>Per entry, that&apos;s a median of
            {' '}{formatNumberWithCommas(Math.round(stats.wordcountAvg * 10) / 10)} words
            {' '}in {formatTimeInterval(stats.writeupTimeExactAvg)}
            {' '}across {formatTimeInterval(stats.writeupTimeTotalAvg)},
            {' '}at {Math.round(stats.writeupSpeedAvg * 10) / 10} wpm.
        </p> }
        <ExploreRatings stats={ stats } from={ props.from } until={ props.until } />
        {prepareNudge}
    </div>
}

const ExploreAllTime = (): ReactElement => <>
    <div id="explore-user-stats">
        <EntryRetriever>
            <ExploreUserStats />
        </EntryRetriever>
    </div>
    <TopPeopleThumbnailList maxItems={ 3 } type="all" />
    <div className="graph-placeholder">
        <PersonRetriever>
            <Graph
                context={ PersonContext }
                action={ { type: 'GRAPH_COUNTRIES_BAR' } }
                transform={ addMentionsPerCitizen } />
        </PersonRetriever>
    </div>
</>

export const ExplorePage = (): ReactElement => {
    const { appState, appDispatch } = useContext(AppContext)
    const currentTab = appState.explore?.tab || 'recent'
    const changeTab = (tab: ExploreTab) => appDispatch({ type: 'EXPLORE_SELECT_TAB', tab })
    return <>
        <Tabs tabs={ exploreTabs } currentTab={ currentTab } changeTab={ changeTab } />
        { currentTab === 'help' && <ExploreHelp /> }
        { currentTab === 'allTime' && <ExploreAllTime />}
        { currentTab === 'recent' && <ExploreRecent />}
    </>
}

export const MIN_DURATION_FOR_LINE_GRAPH = 30

const ExploreDateRangeTitle = (props: { spanId?: number, start: number, end: number }): ReactElement => {
    const { appState, appDispatch } = useContext(AppContext)
    const navigate = useNavigate()
    const recent = formatDate(new Date(formatDate()).getTime() - RECENT_PEOPLE_CLOUD_DAYS * 24 * 60 * 60 * 1000)
    const today = formatDate()
    const savedStart = formatDate(props.start * 1000)
    const savedEnd = formatDate(props.end * 1000)
    const [startInput, setStartInput] = useState(savedStart)
    const [endInput, setEndInput] = useState(savedEnd)
    const [editing, setEditing] = useState(false)
    const apply = () => {
        appDispatch({ type: 'EXPLORE_SET_DATE_RANGE',
            startDate: startInput !== recent ? startInput : undefined,
            endDate: endInput !== today ? endInput : undefined })
        if (props.spanId) void navigate('/people')
        setEditing(false)
    }
    const applyAndClose = (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) apply()
    }
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') apply()
    }
    const reset = () => {
        appDispatch({ type: 'EXPLORE_SET_DATE_RANGE', startDate: undefined, endDate: undefined })
        if (props.spanId) void navigate('/people')
    }
    const enterEditing = () => {
        setStartInput(savedStart)
        setEndInput(savedEnd)
        setEditing(true)
    }
    if (editing) {
        return <div className="explore-date-range" onBlur={ applyAndClose } onKeyDown={ onKeyDown }>
            <input
                type="date"
                value={ startInput }
                onChange={ e => setStartInput(e.target.value) }
                autoFocus />
            <span> to </span>
            <input
                type="date"
                value={ endInput }
                onChange={ e => setEndInput(e.target.value) } />
        </div>
    }
    if (props.spanId) {
        return <InfiniteScrollSpanHeader spanId={ props.spanId } onEdit={ enterEditing } />
    }
    return <h3>
        { savedEnd === today
            ? <>Since {formatDate(props.start * 1000, 'long')}</>
            : <>Between {formatDate(props.start * 1000, 'long')} and {formatDate(props.end * 1000, 'long')}</>
        }
        {' '}<FontAwesomeIcon icon="pen" className="clickable" onClick={ enterEditing } />
        { (!!appState.explore?.startDate || !!appState.explore?.endDate)
          && <>{' '}<FontAwesomeIcon icon="rotate-left" className="clickable" onClick={ reset } /></> }
    </h3>
}

export const ExploreRecent = (): ReactElement => {
    const { appState } = useContext(AppContext)
    const { state: entryState } = useContext(EntryContext)
    const spanId = Number(new URLSearchParams(useLocation().search).get('spanId')) || undefined
    const span = spanId ? entryState.spans?.[spanId] : undefined
    const defaultStart = formatDate(new Date(formatDate()).getTime() - RECENT_PEOPLE_CLOUD_DAYS * 24 * 60 * 60 * 1000)
    const defaultEnd = formatDate()
    /** Unix timestamp in seconds */
    const start = span ? span.startTimestamp : new Date(appState.explore?.startDate || defaultStart).getTime() / 1000
    /** Unix timestamp in seconds */
    const end = span ? span.endTimestamp : new Date(appState.explore?.endDate || defaultEnd).getTime() / 1000
    const endIsToday = span ? !!span.spansNow : (appState.explore?.endDate || defaultEnd) === defaultEnd
    const durationDays = (end - start) / (24 * 60 * 60)
    return <>
        { spanId && <EntryRetriever><SpanLoader /></EntryRetriever> }
        <ExploreDateRangeTitle spanId={ spanId } start={ start } end={ end } />
        <div id="explore-user-stats">
            <EntryRetriever>
                <ExploreUserStats from={ start * 1000 } until={ end * 1000 } />
            </EntryRetriever>
        </div>
        <RecentPeopleThumbnailList
            date={ start }
            until={ endIsToday ? undefined : end }
            maxItems={ 3 }
            spanId={ spanId } />
        <div>
            <PersonRetriever>
                <Graph
                    context={ PersonContext }
                    action={ { type: 'GRAPH_WORDS_CLOUD_BETWEEN_DATES', start, end } } />
            </PersonRetriever>
            { durationDays >= MIN_DURATION_FOR_LINE_GRAPH && <PersonRetriever>
                <Graph
                    context={ PersonContext }
                    action={ { type: 'GRAPH_PEOPLE_LINE_BETWEEN_DATES', start, end } } />
            </PersonRetriever> }
            <PersonRetriever>
                <Graph
                    context={ PersonContext }
                    action={ { type: 'GRAPH_PEOPLE_CLOUD_BETWEEN_DATES', start, end } } />
            </PersonRetriever>
        </div>
    </>
}

export default ExplorePage
